<template>
    <el-collapse v-model="activeName" accordion>
        <el-collapse-item :title="referenceSection.name" name="1" class="section-accordion">
            <template v-if="isNCBRepeatableSection && ncbSegregationCheckQuestion">
                <ncbsegregationcheck
                    :question="ncbSegregationCheckQuestion"
                    :form="form"
                    :sections="filteredSections"
                ></ncbsegregationcheck>
            </template>
            <el-table
                :data="filteredTableData"
                header-cell-class-name="table-head-column"
                cell-class-name="line-clamp"
                :show-summary="hasSummary"
                :summary-method="getSummaries"
            >
                <el-table-column
                    class-name="action-column"
                    prop="operations"
                    :label="$t('Operations')"
                    width="120"
                >
                    <template slot-scope="scope">
                        <div class="repeatable-action-buttons">
                            <div>
                                <div>
                                    <el-button
                                        class="up-down-action"
                                        icon="el-icon-top"
                                        size="mini"
                                        circle
                                        :disabled="scope.$index == 0"
                                        @click="onUpDownClick(scope.$index, 'up')"
                                    >
                                    </el-button>
                                </div>
                                <div>
                                    <el-button
                                        class="up-down-action"
                                        icon="el-icon-bottom"
                                        size="mini"
                                        circle
                                        :disabled="scope.$index == filteredTableData.length - 1"
                                        @click="onUpDownClick(scope.$index, 'down')"
                                    >
                                    </el-button>
                                </div>
                            </div>
                            <el-button
                                icon="el-icon-edit"
                                size="small"
                                circle
                                @click="handleEditButtonClick(scope.$index)"
                            ></el-button>
                            <el-button
                                icon="el-icon-delete"
                                size="small"
                                circle
                                @click="handleDeleteButtonClick(scope.$index)"
                            ></el-button>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column
                    v-for="column in tableColumns"
                    :key="column.prop"
                    :prop="column.prop"
                >
                    <template slot="header" slot-scope="columnData">
                        <div
                            v-if="isUNIDColumn(column)"
                            class="search-column"
                            :align="columnData.align"
                        >
                            <span v-if="searchVisible">
                                <el-input
                                    v-model="searchKey"
                                    autofocus
                                    :placeholder="column.label"
                                ></el-input>
                            </span>
                            <span v-else>
                                {{ column.label }}
                            </span>
                            <i
                                class="fa cursor-pointer"
                                :class="searchVisible ? 'fa-times' : 'fa-search'"
                                @click="searchVisible = !searchVisible"
                            ></i>
                        </div>
                        <div v-else>
                            {{ column.label }}
                        </div>
                    </template>
                    <template slot-scope="slotData">
                        <el-tooltip
                            placement="top-start"
                            :content="clampTooltipContent(slotData.row[column.prop])"
                        >
                            <span class="column-content">
                                {{ getColumnValue(slotData.row, column) }}
                            </span>
                        </el-tooltip>
                    </template>
                </el-table-column>
            </el-table>

            <el-button
                v-if="!currentSection"
                size="small"
                type="primary"
                class="btn-add-new"
                @click="handleAddNewButtonClick"
                >{{ $t('Add New') }}</el-button
            >

            <!-- This is used to show form view for selected repeatable section -->
            <el-dialog
                :title="currentSection ? currentSection.name : null"
                :visible.sync="repeatableSectionModalVisible"
                append-to-body
                v-loading="form.loading"
                :close-on-click-modal="false"
                class="repeatable-section-dlg"
            >
                <div>
                    <question
                        v-for="question in currentSectionQuestions"
                        :question="question"
                        :key="question.id"
                        :id="'q' + question.id"
                        :visitId="visitId"
                        :form="form"
                        :sections="sections"
                        :section="currentSection"
                        :calcMethod="calculatedQuestions(question)"
                        :visit="visit"
                    />

                    <p v-show="errorMessage" class="error-message">* {{ errorMessage }}</p>
                </div>

                <div slot="footer" class="dialog-footer">
                    <el-button class="el-button--primary" @click="handleSaveButtonClick">{{
                        $t('Complete')
                    }}</el-button>
                    <el-button @click="handleCancelButtonSection">{{ $t('Cancel') }}</el-button>
                </div>
            </el-dialog>
        </el-collapse-item>
    </el-collapse>
</template>

<script>
import eventBus from '@app/src/lib/helpers/eventbus';
import { getQuestionValueLabel } from '@app/src/lib/util/section';
import { getSummaries, calculatedQuestions } from '@app/src/lib/calculator/formMethods';
import questionComponent from './question.vue';
import ncbsegregationcheck from './ncbsegregationcheck.vue';

export default {
    name: 'repeatableSection',
    props: {
        sections: { type: Array, required: true },
        repeatableSections: { type: Array, required: true },
        referenceSection: { type: Object, required: true },
        currentSection: { type: Object },
        visitId: { type: String },
        form: { type: Object, required: true },
        activeSection: null,
        activeQuestion: null,
        visit: null,
    },
    components: {
        question: questionComponent,
        ncbsegregationcheck,
    },
    data() {
        return {
            activeName: '1',
            searchVisible: false,
            searchKey: null,
            errorMessage: null,
            repeatableSectionModalVisible: false,
        };
    },
    mounted() {
        this.setQuestionsToggleVisiblity([this.referenceSection], false);

        const $section = document.getElementById(`${this.referenceSection.id}_sctn`);

        if ($section) {
            $section.style.setProperty(
                'display',
                this.referenceSection.default_visibility ? 'block' : 'none',
                'important'
            );
        }
    },
    computed: {
        tableColumns() {
            if (!this.referenceSection) {
                return [];
            }

            let { questions = [] } = this.referenceSection;
            questions = questions.filter((question) => !question.isNCBSegrationCheck);

            return questions.reduce((acc, ques) => {
                if (ques.show_in_table && this.isAllowedQuestionType(ques)) {
                    acc.push({
                        prop: ques.id,
                        label: ques.caption,
                        tag: ques.tag,
                    });
                }

                return acc;
            }, []);
        },
        tableData() {
            return this.filteredSections.map((section) => {
                let { questions = [] } = section;
                questions = questions.filter((question) => !question.isNCBSegrationCheck);
                return questions.reduce((acc, curr) => {
                    acc[curr.id] = getQuestionValueLabel(curr);

                    return acc;
                }, {});
            });
        },
        filteredTableData() {
            let key = this.searchKey;
            if (!key) {
                return this.tableData;
            }
            key = key.toLowerCase();
            return this.tableData.filter((item) => {
                let match = false;
                // eslint-disable-next-line
                for (var p in item) {
                    if (item[p] && item[p].toLowerCase().indexOf(key) > -1) {
                        match = true;
                        break;
                    }
                }
                return match;
            });
        },
        filteredSections() {
            return this.repeatableSections.filter(
                (section) => !section.clientData?.isCreate && !section.clientData?.isEmpty
            );
        },
        currentSectionQuestions() {
            return (
                this.currentSection?.questions.filter(
                    (question) => !question.isNCBSegrationCheck
                ) || []
            );
        },
        isNCBRepeatableSection() {
            return this.referenceSection?.questions.some((question) => question.isNCBType) || false;
        },
        ncbSegregationCheckQuestion() {
            return (
                this.referenceSection?.questions.find((question) => question.isNCBSegrationCheck) ||
                null
            );
        },
        hasSummary() {
            const { questions } = this.referenceSection;
            return (
                questions.length > 0 &&
                questions.filter((question) => question.calc_method).length > 0
            );
        },
    },
    watch: {
        repeatableSectionModalVisible(value) {
            if (value === false) {
                eventBus.$emit('cancelEditingSection', this.currentSection);
            }
        },
    },
    methods: {
        calculatedQuestions(question) {
            const formQuestions = [].concat(this.currentSection.questions);
            return calculatedQuestions(question, formQuestions /* [this.currentSection] */);
        },
        getSummaries(param) {
            return getSummaries(param);
        },
        handleEditButtonClick(index) {
            eventBus.$emit('startEditingSection', this.filteredSections[index]);
            this.$nextTick(() => {
                this.setQuestionsToggleVisiblity([this.filteredSections[index]], true);
                this.repeatableSectionModalVisible = true;
            });
        },
        handleAddNewButtonClick() {
            eventBus.$emit('addNewSection', this.referenceSection);
            this.$nextTick(() => {
                this.setQuestionsToggleVisiblity([this.referenceSection], true);
                this.repeatableSectionModalVisible = true;
            });
        },
        handleDeleteButtonClick(index) {
            eventBus.$emit('deleteSection', this.filteredSections[index]);
            this.setQuestionsToggleVisiblity([this.filteredSections[index]], false);
        },
        handleCancelButtonSection() {
            this.repeatableSectionModalVisible = false;
        },
        handleSaveButtonClick() {
            const isValid = !this.currentSection.questions.find((question) => {
                let isValidQuestion = true;

                if (question.mandatory && !question.data.return_value) {
                    isValidQuestion = false;
                }

                return !isValidQuestion;
            });

            if (isValid) {
                this.repeatableSectionModalVisible = false;
                eventBus.$emit('saveSection', this.currentSection);
            } else {
                this.errorMessage = this.$t('Please fill the mandatory fields');
            }
        },
        isAllowedQuestionType(question) {
            return !(question.isPageBreak || question.isMedia || question.isSignature);
        },
        clampTooltipContent(content) {
            const max = 80;

            if (content?.length > max) {
                return `${content
                    .split(' ')
                    .reduce((acc, curr) => {
                        if (acc.length < max) {
                            acc += `${curr} `;
                        }

                        return acc;
                    }, '')
                    .trim()}...`;
            }

            return content;
        },
        setQuestionsToggleVisiblity(sectionsArray, visible) {
            sectionsArray = sectionsArray || [];
            for (let i = 0, l = sectionsArray.length; i < l; i += 1) {
                const section = sectionsArray[i];

                if (section) {
                    // section icindeki sorular
                    for (let j = 0, k = section?.questions?.length; j < k; j += 1) {
                        section.questions[j].visible = visible;
                        this.form.setHiddenQuestions(section.questions[i].id, visible);
                    }

                    this.setQuestionsToggleVisiblity(
                        this.sections.filter((item) => item.parent_id === section.id),
                        visible
                    );
                }
            }
        },
        /**
         * TODO ASIM:
         * Burasi sadece NCB icin yazilmistir
         */
        getColumnValue(row, column) {
            if (this.isUNIDColumn(column)) {
                return row[column.prop]?.replace(/(\d+-\d\s?)(.*)/, '$1');
            }
            return row[column.prop];
        },
        isUNIDColumn(column) {
            return column.label.toLowerCase() == 'un/id number';
        },
        onUpDownClick(index, type = 'up') {
            // let ques = this.currentSection.questions.find()
            const sect = this.filteredSections[index];

            if (type == 'up') {
                sect.section_sort_index -= 1;
            } else if (type == 'down') {
                sect.section_sort_index += 1;
            }

            setTimeout(() => {
                eventBus.$emit('saveSection', sect);
            }, 250);
            // eventBus.$emit('saveSection', this.currentSection);
        },
    },
};
</script>

<style lang="scss">
.line-clamp {
    .cell {
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
}

.action-column {
    .cell {
        overflow: visible;
    }

    .repeatable-action-buttons {
        display: flex;
        align-items: center;
        justify-content: space-evenly;

        .el-button {
            margin: 0px;
        }

        .up-down-action {
            padding: 3px;
        }
    }
}

.btn-add-new {
    margin-top: 20px;
}

.form-head {
    margin: 20px 0;
    font-size: 17px;
}

.section-name {
    border-bottom: solid 1px rgb(235, 238, 245);
    font-size: 13px;
    font-weight: 500;
    padding: 10px 0;
}

.no-data-message {
    font-weight: 500;
    padding: 10px 0;
}

.column-content {
    cursor: default;
}
.search-column {
    display: flex;
    align-items: center;

    input.el-input__inner {
        height: 23px;
        padding: 2px;
        line-height: 18px;
    }

    i {
        margin-left: 10px;
    }
}

.repeatable-section-dlg {
    .el-dialog {
        width: fit-content !important;
        min-width: 50%;
    }
}
</style>
